import { createAsyncThunk, createAction, createReducer } from '@reduxjs/toolkit'
import { API, SCOPE_SAVED_SEARCHES } from '../../constants'
import { application } from '../../services/application'
import { INITIAL_LIST_STATE_EXTENDED } from '../helpers/common'
import { extendBuilderWithAsyncAction } from '../helpers/sharedCases'

export const INITIAL_STATE = {
  ...INITIAL_LIST_STATE_EXTENDED,
  order: ['createdAt', 'desc'],
  params: {},
  groups: [],
  items: [],
  count: 0,
  userDetail: null,
  savedSearchesSelected: [],
  pending: false,
  savedSearchStats: {},
}

function flattenGroups(groups = []) {
  return groups.reduce((acc, group) => acc.concat(group.items || []), [])
}

/**
 * Fetch saved searches grouped by organization for a registered user.
 */
const fetchList = createAsyncThunk(
  `${SCOPE_SAVED_SEARCHES}/fetchList`,
  async (_, { getState }) => {
    const { params } = getState().registeredUserDetail
    return application.call(API.SAVED_SEARCHES.GET, {
      userName: params?.filterByUserId || params?.userName,
    })
  }
)

const setParams = createAction(`${SCOPE_SAVED_SEARCHES}/setParams`)
const setPending = createAction(`${SCOPE_SAVED_SEARCHES}/setPending`)

const saveSearchAction = createAsyncThunk(
  `${SCOPE_SAVED_SEARCHES}/saveSearch`,
  data => {
    const {
      id,
      searchSavedOwner,
      title,
      notification,
      notificationFrequency,
      notificationNumber,
    } = data
    return application.call(API.SAVED_SEARCHES.UPDATE, {
      id,
      searchSavedOwner,
      title,
      notification,
      notificationFrequency,
      notificationNumber,
    })
  }
)

const deleteSearchAction = createAsyncThunk(
  `${SCOPE_SAVED_SEARCHES}/deleteSearch`,
  ({ id, savedSearchOwner }) => {
    return application.call(API.SAVED_SEARCHES.DELETE, { id, savedSearchOwner })
  }
)

const saveDataAfterDeleteSearchAction = createAction(
  `${SCOPE_SAVED_SEARCHES}/saveDataAfterDeleteSearch`
)
function onSaveDataAfterDeleteSearch(state, action) {
  const id = action.payload.id
  const groups = state.groups
    .map(group => ({
      ...group,
      items: group.items.filter(item => item.id !== id),
      stats: {
        ...group.stats,
        savedSearchCount: Math.max(0, (group.stats?.savedSearchCount || 0) - (group.items.some(i => i.id === id) ? 1 : 0)),
      },
    }))
    .filter(group => group.items.length > 0)

  Object.assign(state, {
    groups,
    items: flattenGroups(groups),
    count: flattenGroups(groups).length,
    savedSearchesSelected: state.savedSearchesSelected.filter(s => s !== id),
  })
}

const deleteBulkSearchAction = createAsyncThunk(
  `${SCOPE_SAVED_SEARCHES}/deleteBulkSearch`,
  ({ ids, type, savedSearchOwner }) => {
    return application.call(API.SAVED_SEARCHES.DELETE_BULK, {
      ids,
      type,
      savedSearchOwner,
    })
  }
)

const saveDataAfterDeleteBulkSearchAction = createAction(
  `${SCOPE_SAVED_SEARCHES}/saveDataAfterDeleteBulkSearch`
)
function onSaveDataAfterDeleteBulkSearch(state, action) {
  const ids = new Set(action.payload.ids || [])
  const groups = state.groups
    .map(group => {
      const remaining = group.items.filter(item => !ids.has(item.id))
      return {
        ...group,
        items: remaining,
        stats: {
          ...group.stats,
          savedSearchCount: remaining.length,
        },
      }
    })
    .filter(group => group.items.length > 0)

  Object.assign(state, {
    groups,
    items: flattenGroups(groups),
    count: flattenGroups(groups).length,
    savedSearchesSelected: [],
  })
}

const findOneSearchAction = createAsyncThunk(
  `${SCOPE_SAVED_SEARCHES}/findOneSearch`,
  params => {
    return application.call(API.SAVED_SEARCHES.FIND_ONE, params)
  }
)

const turnOnOffNotificationsAction = createAsyncThunk(
  `${SCOPE_SAVED_SEARCHES}/turnOnOffNotifications`,
  ({
    savedSearchOwner,
    pursuitNotices,
    savedSearchNotices,
    teamUpdates,
    savedSearchTypeNotices,
    consolidateNotificationSelect,
    consolidateNotificationNumber,
    taskReminders,
    onEmailNotifications,
    pursuitDailyRecap,
    savedSearchDailyRecap,
    teamUpdatesDailyRecap,
    taskRemindersDailyRecap,
    personalTaskRemindersDailyRecap,
  }) => {
    return application.call(API.USERS.SET_NOTIFICATION_SETTINGS, {
      savedSearchOwner,
      pursuitNotices,
      savedSearchNotices,
      teamUpdates,
      savedSearchTypeNotices,
      consolidateNotificationSelect,
      consolidateNotificationNumber,
      taskReminders,
      onEmailNotifications,
      pursuitDailyRecap,
      savedSearchDailyRecap,
      teamUpdatesDailyRecap,
      taskRemindersDailyRecap,
      personalTaskRemindersDailyRecap,
    })
  }
)

const toggleWatchAction = createAsyncThunk(
  `${SCOPE_SAVED_SEARCHES}/toggleWatch`,
  ({ id, savedSearchOwner }) => {
    return application.call(API.SAVED_SEARCHES.TOGGLE_WATCH, {
      id,
      savedSearchOwner,
    })
  }
)

const findOne = createAsyncThunk('registeredUsers/findOne', params => {
  return application.call(API.USERS.FIND_ONE, params)
})
function onFindOne(state, action) {
  Object.assign(state, {
    userDetail: action.payload,
  })
}

const addGroupSavedSearchesAction = createAction(
  `${SCOPE_SAVED_SEARCHES}/addGroupSavedSearches`
)
function onAddGroupSavedSearches(state, action) {
  const groupIds = action.payload || []
  const selected = new Set([...state.savedSearchesSelected, ...groupIds])
  Object.assign(state, {
    savedSearchesSelected: [...selected],
  })
}

const removeGroupSavedSearchesAction = createAction(
  `${SCOPE_SAVED_SEARCHES}/removeGroupSavedSearches`
)
function onRemoveGroupSavedSearches(state, action) {
  const groupIds = new Set(action.payload || [])
  Object.assign(state, {
    savedSearchesSelected: state.savedSearchesSelected.filter(
      id => !groupIds.has(id)
    ),
  })
}

const addOneSavedSearchesAction = createAction(
  `${SCOPE_SAVED_SEARCHES}/addOneSavedSearches`
)
function onAddOneSavedSearches(state, action) {
  if (state.savedSearchesSelected.includes(action.payload.id)) return
  Object.assign(state, {
    savedSearchesSelected: [...state.savedSearchesSelected, action.payload.id],
  })
}

const removeOneSavedSearchesAction = createAction(
  `${SCOPE_SAVED_SEARCHES}/removeOneSavedSearches`
)
function onRemoveOneSavedSearches(state, action) {
  Object.assign(state, {
    savedSearchesSelected: state.savedSearchesSelected.filter(
      id => id !== action.payload.id
    ),
  })
}

const restartStateAction = createAction(`${SCOPE_SAVED_SEARCHES}/restartState`)
function onRestartState(state) {
  return Object.assign(state, {
    groups: [],
    items: [],
    count: 0,
    savedSearchesSelected: [],
  })
}

const getSavedSearchStats = createAsyncThunk(
  `${SCOPE_SAVED_SEARCHES}/getSavedSearchStats`,
  params => {
    return application.call(API.SAVED_SEARCHES.GET_STATS, params)
  }
)
const onGetSavedSearchStats = (state, action) => {
  return Object.assign(state, {
    savedSearchStats: action.payload,
    pending: false,
  })
}

export const actions = {
  fetchList,
  setParams,
  setPending,
  saveSearchAction,
  deleteSearchAction,
  deleteBulkSearchAction,
  toggleWatchAction,
  findOneSearchAction,
  findOne,
  turnOnOffNotificationsAction,
  addGroupSavedSearchesAction,
  removeGroupSavedSearchesAction,
  addOneSavedSearchesAction,
  removeOneSavedSearchesAction,
  saveDataAfterDeleteSearchAction,
  saveDataAfterDeleteBulkSearchAction,
  restartStateAction,
  getSavedSearchStats,
}

export default createReducer(INITIAL_STATE, builder => {
  extendBuilderWithAsyncAction(builder, saveSearchAction)
  extendBuilderWithAsyncAction(builder, deleteSearchAction)
  extendBuilderWithAsyncAction(builder, deleteBulkSearchAction)
  extendBuilderWithAsyncAction(builder, toggleWatchAction)
  extendBuilderWithAsyncAction(builder, turnOnOffNotificationsAction, {
    onSuccess: (state, action) => {
      if (action.payload) {
        Object.assign(state, {
          userDetail: { ...state.userDetail, ...action.payload },
          pending: false,
        })
      } else {
        Object.assign(state, { pending: false })
      }
    },
  })
  extendBuilderWithAsyncAction(builder, getSavedSearchStats, {
    onSuccess: onGetSavedSearchStats,
  })

  builder
    .addCase(fetchList.pending, state => {
      Object.assign(state, { pending: true })
    })
    .addCase(fetchList.fulfilled, (state, action) => {
      const groups = action.payload?.groups || []
      const items = flattenGroups(groups)
      Object.assign(state, {
        groups,
        items,
        count: items.length,
        pending: false,
      })
    })
    .addCase(fetchList.rejected, state => {
      Object.assign(state, { pending: false })
    })
    .addCase(setParams, (state, action) => {
      Object.assign(state, {
        params: { ...state.params, ...action.payload },
      })
    })
    .addCase(setPending, (state, action) => {
      Object.assign(state, { pending: action.payload })
    })
    .addCase(findOne.fulfilled, onFindOne)
    .addCase(addGroupSavedSearchesAction, onAddGroupSavedSearches)
    .addCase(removeGroupSavedSearchesAction, onRemoveGroupSavedSearches)
    .addCase(addOneSavedSearchesAction, onAddOneSavedSearches)
    .addCase(removeOneSavedSearchesAction, onRemoveOneSavedSearches)
    .addCase(saveDataAfterDeleteSearchAction, onSaveDataAfterDeleteSearch)
    .addCase(
      saveDataAfterDeleteBulkSearchAction,
      onSaveDataAfterDeleteBulkSearch
    )
    .addCase(restartStateAction, onRestartState)
})
